<script lang="ts" setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  getCurrentInstance,
} from "vue";
import {
  EditTripContactPayload,
  ReservationDetail,
  SharedList,
} from "@/models/dto";
import { phoneFormatFilterInternational } from "@/utils/phone";
import type { Customer } from "@/models/dto/Customer";
import ReservationDetailShareDialog from "@/components/ReservationDetailShareDialog.vue";
import ReservationDetailTripContactForm from "@/components/ReservationDetailTripContactForm.vue";
import ReservationDetailContactInfomationVerifySMS from "@/components/ReservationDetailContactInfomationVerifySMS.vue";
import reservation from "@/services/reservation";
import trip from "@/services/trip";
import { toKebab } from "@/utils/string";
import auth from "../../store/modules/auth";
import { ReservationStatusKey, SplitFeatureFlag } from "@/utils/enum";
import customerService from "@/services/customer"; // renamed to avoid conflict
import { EventBus } from "@/utils/eventBus";
import ContactList from "./ContactList.vue";
import type {
  ContactGroup,
  Contact,
  ContactCurrentUserPermissions,
} from "@/models/dto/ContactGroup";
import reservationDetail from "@/services/reservationDetail";
import { useSplit } from "@/composables/useSplit";
import AddContactToReservation from "./AddContactToReservation.vue";
import { ContactType } from "./utils/helpers";
import ConfirmRemoveContactDialog from "./ConfirmRemoveContactDialog.vue";
import CreateEditContact from "@/components/Contact/CreateEditContact.vue";
import { useDisplay } from "@/composables/useDisplay";

const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);
const { xs } = useDisplay();
const fullscreen = computed(() => xs.value);

// Props
const props = defineProps<{
  reservation: ReservationDetail;
  sharedList: SharedList;
  allowUserManagement?: boolean;
  isTripContactModalOpen?: boolean;
  isRiderModalOpen?: boolean;
  isVerifyPhoneModalOpen?: boolean;
}>();

// Emits
const emit = defineEmits<{
  (e: "refresh-shared-list"): void;
  (e: "refresh"): void;
}>();

// Refs
const isShareReservationDialogOpen = ref(false);
const isTripContactDialogOpen = ref(false);
const tripContactCustomer = ref<Customer | null>(null);
const customerData = ref<Customer | null>(null); // renamed from customer to customerData
const contactGroups = ref<ContactGroup[]>([]);
const currentUserCreatePermissions = ref<ContactCurrentUserPermissions | null>(
  null
);
const isUserRoleManagementEnabled = ref(false);
const isLoading = ref(false);
const showContactDialog = ref(false);
const editingContact = ref<Contact | null>(null);
const showRemoveContactDialog = ref(false);

// Split.io integration
const { isFeatureEnabled } = useSplit();

// Watchers
watch(
  () => props.isTripContactModalOpen,
  (val) => {
    isTripContactDialogOpen.value =
      !showTripContact.value && props.isTripContactModalOpen;
  }
);

watch(
  () => props.isRiderModalOpen,
  (val) => {
    isShareReservationDialogOpen.value = props.isRiderModalOpen;
  }
);

// Computed
const isFinished = computed(
  () => props.reservation?.reservationStatus === ReservationStatusKey.Finished
);

const isUserSMSVerified = computed(() => auth.customer?.smsConfirmed);

const isCustomerSMSEnabled = computed(() => customerData.value?.smsConfirmed);

const isTripContactSMSEnabled = computed(
  () => tripContactCustomer.value?.smsConfirmed
);

const bookingContact = computed(
  (): Contact => ({
    id: customerData.value?.customerId,
    firstName: customerData.value?.firstName,
    lastName: customerData.value?.lastName,
    email: customerData.value?.email,
    phone: customerData.value?.phone,
  })
);

const tripContact = computed(
  (): Contact => ({
    id: tripContactCustomer.value?.customerId,
    firstName: tripContactCustomer.value?.firstName,
    lastName: tripContactCustomer.value?.lastName,
    email: tripContactCustomer.value?.email,
    phone: tripContactCustomer.value?.phone,
  })
);

const showTripContact = computed(
  () =>
    props.reservation?.tripContact?.customerId &&
    props.reservation?.tripContact?.customerId !==
      customerData.value?.customerId
);

const invitedUserContacts = computed((): Contact[] => {
  if (!props.sharedList.length) return [];

  let sharedList = props.sharedList;
  if (!props.allowUserManagement) {
    sharedList = sharedList.filter((user) => user.customerId === auth.userId);
  } else {
    const tripContactId =
      tripContact.value?.id || props.reservation?.tripContact?.customerId;
    sharedList = sharedList.filter(
      (user) =>
        ![tripContactId, bookingContact.value.id].includes(user.customerId)
    );
  }

  return sharedList.map((user) => ({
    id: user.customerId,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone,
  }));
});

const contacts = computed(() => {
  const contacts = [];
  contacts.push({
    label: t("reservationDetail.contactInformation.BOOKING_CONTACT"),
    list: [bookingContact.value],
  });

  if (showTripContact.value) {
    contacts.push({
      label: t("reservationDetail.contactInformation.TRIP_CONTACT"),
      list: [tripContact.value],
    });
  }

  if (invitedUserContacts.value.length) {
    contacts.push({
      label: t("reservationDetail.contactInformation.RIDERS_WITH_ACCESS"),
      list: invitedUserContacts.value,
    });
  }

  return contacts;
});

// Methods
const refreshContacts = async () => {
  if (isUserRoleManagementEnabled.value) {
    await loadContactGroups();
    editingContact.value = null;
  } else {
    await loadCustomer();
    await loadTripContact();
  }
};

const loadContactGroups = async () => {
  isLoading.value = true;
  try {
    const response = await reservationDetail.getContacts(
      props.reservation.reservationId
    );
    contactGroups.value = response.data.contactGroups;
    currentUserCreatePermissions.value =
      response.data.currentUserCreatePermissions;
  } finally {
    isLoading.value = false;
  }
};

const loadTripContact = async () => {
  if (!props.reservation?.tripContact?.customerId) return;

  const response = await customerService.byId(
    props.reservation.tripContact.customerId
  );
  tripContactCustomer.value = response.data.customer;
};

const loadCustomer = async () => {
  const response = await customerService.byId(
    props.reservation?.customer?.customerId
  );
  customerData.value = response.data.customer;
};

const openTripContactDialog = () => {
  isTripContactDialogOpen.value = true;
};

const remove = async (userId: number) => {
  if (!userId) return;

  if (userId === tripContact.value?.id) {
    const tripId = props.reservation.tripId;
    const payload: EditTripContactPayload = {
      tripContact: {
        customerId: null,
      },
    };
    await trip.editTripContact(tripId, payload);
    tripContactCustomer.value = null;
    emit("refresh");
    return;
  }

  await reservation.unshare(props.reservation.reservationId, userId);
  emit("refresh-shared-list");
};

const isSMSEnabled = (contactTypeLabel: string): boolean => {
  if (contactTypeLabel === "Booking Contact") {
    return isCustomerSMSEnabled.value;
  } else if (contactTypeLabel === "Trip Contact") {
    return isTripContactSMSEnabled.value;
  }
  return false;
};

// Lifecycle hooks
onMounted(async () => {
  isUserRoleManagementEnabled.value = await isFeatureEnabled(
    SplitFeatureFlag.UserRoleManagement
  );
  await refreshContacts();

  EventBus.$on("refresh-contacts", refreshContacts);
  isTripContactDialogOpen.value =
    !showTripContact.value && props.isTripContactModalOpen;
  isShareReservationDialogOpen.value = props.isRiderModalOpen;
});

onBeforeUnmount(() => {
  EventBus.$off("refresh-contacts");
});

const onRemoveContact = async () => {
  editingContact.value = null;
  await refreshContacts();
};

const onUpdateContact = async () => {
  await refreshContacts();
  showContactDialog.value = false;
};

const handleContactDialogInput = (val: boolean) => {
  showContactDialog.value = val;
  if (!val) {
    editingContact.value = null;
  }
};

const handleRemoveDialogInput = (val: boolean) => {
  showRemoveContactDialog.value = val;
};

const handleContactEditClick = (contact: Contact) => {
  editingContact.value = contact;
  showContactDialog.value = true;
};

const handleRemoveContactClick = (contact: Contact) => {
  editingContact.value = contact;
  showRemoveContactDialog.value = true;
  showContactDialog.value = false;
};

const handleRemoveContactCancel = () => {
  showRemoveContactDialog.value = false;
  if (fullscreen.value) {
    showContactDialog.value = true;
  }
};

const contactType = ContactType.Reservation;
</script>

<template>
  <div class="d-flex flex-column">
    <h3 class="margin-b-0 subtitle-1">
      {{ $t("reservationDetail.contactInformation.TITLE") }}
    </h3>
    <ReservationDetailContactInfomationVerifySMS
      v-if="!isUserSMSVerified"
      :is-verify-phone-modal-open="isVerifyPhoneModalOpen"
    />
    <template v-if="isUserRoleManagementEnabled">
      <ContactList
        :contact-groups="contactGroups"
        :is-loading="isLoading"
        :reservation-id="props.reservation.reservationId"
        @click:delete-contact="handleRemoveContactClick"
        @click:edit-contact="handleContactEditClick"
      />
      <CreateEditContact
        :value="showContactDialog"
        @input="handleContactDialogInput"
        :title="$t('tripContact.EDIT_CONTACT').toString()"
        :submit-button-text="$t('common.CONTINUE').toString()"
        :contact-groups="contactGroups"
        :is-edit-mode="true"
        :contact="editingContact"
        :header-action-text="$t('common.CLOSE').toString()"
        :contact-type="contactType"
        :entity-id="props.reservation.reservationId"
        @update:contact="onUpdateContact"
        @click:remove-contact="handleRemoveContactClick"
      />
      <ConfirmRemoveContactDialog
        :value="showRemoveContactDialog"
        @cancel="handleRemoveContactCancel"
        @confirm="onRemoveContact"
        @update:value="handleRemoveDialogInput"
        :contact-to-remove="editingContact"
        :contact-type="contactType"
        :entity-id="props.reservation.reservationId"
      />
      <AddContactToReservation
        v-if="!isLoading && currentUserCreatePermissions?.canAddUser"
        :reservation-id="props.reservation.reservationId"
        :contact-groups="contactGroups"
        :user-create-permissions="currentUserCreatePermissions"
        @refresh="refreshContacts"
      />
    </template>
    <template v-else>
      <template v-for="(contactType, contactTypeIndex) in contacts">
        <h4
          :key="`contact-label-${contactTypeIndex}`"
          class="margin-t-3 font-book text-gray-light"
        >
          {{ contactType.label }}
        </h4>
        <v-row
          v-for="(contact, contactIndex) in contactType.list"
          :key="`contact-row-${contactTypeIndex}-${contact.id}-${contactIndex}`"
          :class="{
            'elevation-0 margin-y-3 border-1 border-solid border-border-gray rounded contact-card-mobile':
              $vuetify.breakpoint.xsOnly,
            'last-of-type:margin-b-0 align-center': $vuetify.breakpoint.smAndUp,
          }"
        >
          <v-col
            v-if="contact.firstName || contact.lastName"
            cols="6"
            sm="6"
            md="3"
            :class="{
              'padding-b-0': $vuetify.breakpoint.xsOnly,
              'padding-b-1': contactIndex !== contactType.list.length - 1,
            }"
          >
            <p class="font-14 margin-t-0">
              {{ $t("reservationDetail.contactInformation.NAME") }}
            </p>
            <p class="margin-t-n1 font-medium">
              {{ contact.firstName }} {{ contact.lastName }}
            </p>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="5"
            :class="{
              'padding-b-0': contact.phone && $vuetify.breakpoint.xsOnly,
              'padding-b-1': contactIndex !== contactType.list.length - 1,
              'padding-b-2': !contact.phone && $vuetify.breakpoint.xsOnly,
            }"
          >
            <p class="font-14 margin-t-0">
              {{ $t("reservationDetail.contactInformation.EMAIL") }}
            </p>
            <p
              class="margin-t-n1 font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis"
            >
              {{ contact.email }}
            </p>
          </v-col>
          <v-col
            v-if="contact.phone"
            cols="12"
            sm="6"
            md="auto"
            :class="{
              'padding-b-0': $vuetify.breakpoint.xsOnly,
              'padding-b-1': contactIndex !== contactType.list.length - 1,
            }"
          >
            <div class="d-flex align-center">
              <label class="font-14  margin-t-0">
                {{ $t('common.PHONE_NUMBER') }}
              </label>
              <CUIcon
                v-if="isSMSEnabled(contactType.label)"
                color="primary"
                view-box="0 0 24 24"
                width="16px"
                height="16px"
                class="margin-l-1 cursor-default"
              >
                verified
              </CUIcon>
            </div>
            <p
              class="margin-t-n1 font-medium"
              :class="{
                'padding-b-2': $vuetify.breakpoint.xsOnly,
              }"
            >
              {{ phoneFormatFilterInternational(contact.phone) }}
            </p>
          </v-col>
          <template
            v-if="allowUserManagement && contact.id !== bookingContact.id"
          >
            <v-spacer />
            <v-col
              class="shrink"
              :class="{ 'delete-icon-mobile': $vuetify.breakpoint.xsOnly }"
            >
              <CUIcon
                :id="`${toKebab(
                  contactType.label
                )}-remove-contact-${contactIndex}`"
                color="gray-mid-light"
                hover-color="error"
                class="margin-b-n3"
                width="32"
                height="32"
                @click="remove(contact.id)"
              >
                delete
              </CUIcon>
            </v-col>
          </template>
        </v-row>
        <div
          v-if="
            contactTypeIndex === 0 && !showTripContact && allowUserManagement
          "
          :key="`contact-label-${contactTypeIndex}-trip-contact`"
          class="d-flex flex-column margin-t-2"
        >
          <p class="align-self-start">
            {{
              $t("reservationDetail.contactInformation.IS_THERE_A_TRIP_CONTACT")
            }}
          </p>
          <p
            class="font-bold text-primary margin-t-0 padding-t-0 cursor-pointer align-self-start"
            @click="openTripContactDialog"
          >
            {{ $t("reservationDetail.contactInformation.ADD_A_TRIP_CONTACT") }}
          </p>
        </div>
      </template>
      <template v-if="allowUserManagement && !isFinished">
        <p
          class="font-bold text-primary cursor-pointer align-self-start"
          @click="isShareReservationDialogOpen = true"
        >
          {{ $t("reservationDetail.contactInformation.SHARE_WITH_RIDERS") }}
        </p>
        <ReservationDetailShareDialog
          v-model="isShareReservationDialogOpen"
          :reservation-id="props.reservation.reservationId"
          :shared-list="sharedList"
          @refresh-shared-list="$emit('refresh-shared-list')"
        />
        <v-dialog
          v-model="isTripContactDialogOpen"
          :fullscreen="$vuetify.breakpoint.xs"
          max-width="500px"
        >
          <ReservationDetailTripContactForm
            :reservations="[props.reservation]"
            :shared-list="sharedList"
            closeable
            @close="isTripContactDialogOpen = false"
          />
        </v-dialog>
      </template>
    </template>
  </div>
</template>

<style scoped>
.contact-card-mobile {
  margin-right: 1px;
  margin-left: 1px;
}

.delete-icon-mobile {
  display: block;
  width: 20px;
  position: absolute;
  right: 12%;
}
</style>
